import {
	type GetAllClientProjectsQuery,
	type GetAllClientProjectsQueryVariables,
	useGetAllClientProjectsLazyQuery,
} from "@repo/server-api";

import { useAsyncSelectCursorPaginatedLazyQuery } from "./AsyncSelectCursorPaginatedLazyQuery";

import type { AsyncSelectSearchHook } from "../BoundAsyncSelect";
import type { IdWithName } from "@repo/shared/common";

export function useAsyncSelectClientProjectsQuery(
	clientId: string | null,
): AsyncSelectSearchHook<IdWithName> {
	const [fetchProjects] = useGetAllClientProjectsLazyQuery();

	const buildVariables = (
		search: string,
		cursor: string | null,
	): GetAllClientProjectsQueryVariables => ({
		clientId: clientId ?? "",
		name: search || null,
		first: 20,
		after: cursor,
	});

	const skipQuery = !clientId;

	const extractItems = (data: GetAllClientProjectsQuery | undefined): IdWithName[] =>
		data?.allProjectIdsOfClient?.nodes?.map((n) => ({
			id: n.id,
			name: n.name,
		})) ?? [];

	const extractEndCursor = (data: GetAllClientProjectsQuery | undefined): string | null =>
		data?.allProjectIdsOfClient?.pageInfo?.endCursor ?? null;

	const extractHasNextPage = (data: GetAllClientProjectsQuery | undefined): boolean =>
		data?.allProjectIdsOfClient?.pageInfo?.hasNextPage ?? false;
	const hook = useAsyncSelectCursorPaginatedLazyQuery<
		GetAllClientProjectsQueryVariables,
		GetAllClientProjectsQuery,
		IdWithName
	>(fetchProjects, buildVariables, extractItems, extractEndCursor, extractHasNextPage);

	if (skipQuery) {
		return {
			...hook,
			execute: async (_search: string, _cursor: string | null) => {},
			resetSearch: async () => {},
			items: [],
			loading: false,
			hasNextPage: false,
			endCursor: null,
		};
	}

	return hook;
}
